/**
 * Featured item card for carousel display
 * Vertical layout: large image with + button, name, price
 */
import { memo, useState } from 'react'
import { Plus, Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { MenuItem } from '@/types/menu'
import { useAppDispatch } from '@/store/hooks'
import { addItem } from '@/store/cartSlice'
import { formatPrice } from '@/utils/formatting'

interface FeaturedItemCardProps {
  item: MenuItem
  onItemClick: (item: MenuItem) => void
}

export const FeaturedItemCard = memo(function FeaturedItemCard({ item, onItemClick }: FeaturedItemCardProps) {
  const dispatch = useAppDispatch()
  const [justAdded, setJustAdded] = useState(false)

  const canQuickAdd = item.itemType === 'single' && (item.modifierGroups?.length || 0) === 0

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (canQuickAdd && item.available) {
      dispatch(
        addItem({
          menuItem: item,
          selectedModifiers: [],
          quantity: 1,
        })
      )
      setJustAdded(true)
      setTimeout(() => setJustAdded(false), 600)
    } else {
      onItemClick(item)
    }
  }

  return (
    <div
      className="shrink-0 w-[180px] sm:w-[200px] cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-2xl"
      role="button"
      tabIndex={0}
      onClick={() => onItemClick(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onItemClick(item)
        }
      }}
    >
      {/* Image container with + button */}
      <div className="relative mb-2.5">
        <div className="w-full h-[140px] sm:h-[156px] bg-muted rounded-2xl overflow-hidden">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-muted" />
          )}
        </div>
        <Button
          variant="white"
          size="icon"
          className={`absolute bottom-2 right-2 rounded-full h-8 w-8 shadow-md border border-border transition-all duration-150 ${
            justAdded ? 'bg-primary text-primary-foreground scale-105' : 'hover:scale-105 active:scale-95'
          }`}
          onClick={handleQuickAdd}
          disabled={!item.available}
          aria-label="Add"
        >
          {justAdded ? (
            <Check className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold leading-snug mb-0.5 line-clamp-2">
        {item.name}
      </h3>

      {/* Price */}
      <p className="text-sm text-muted-foreground">
        {formatPrice(item.price, item.currency)}
      </p>
    </div>
  )
})
